import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, CheckCircle, Eye, ArrowRight, ChevronRight, ChevronLeft, Activity, Moon, Brain } from 'lucide-react';
import Header from '../../components/Header';
import './Homepage.css';

const MEDITATION_IMG = 'https://images.unsplash.com/photo-1775452455306-708bec44d25c?w=800&q=80&auto=format';

/* ══════════════════════════════════════════
   HERO STAGE SLIDES — Türkçe karakterler kusursuz
   ══════════════════════════════════════════ */
const HERO_SLIDES = [
  {
    id: 's1',
    eyebrow: 'Kişisel zihinsel sağlık takibi',
    heading: 'Zihninizi Anlayan\nDijital Sağlık Asistanı',
    desc: 'Stres, uyku, unutkanlık, dikkat ve günlük sağlık verileriniz birlikte değerlendirilerek size özel bir yol haritası oluşturulur.',
    sub: 'Başlamak sadece birkaç dakika sürer.',
    image: 'https://images.unsplash.com/photo-1762341118325-23fece6a2508?w=900&q=85&auto=format&fit=crop',
    overlayTitle: 'Genel Değerlendirme',
    overlayText: 'Son günlerde artan zihinsel yükünüz, kaygı seviyenizin yükselmesine neden olmuş olabilir.',
    overlayHints: [
      { label: 'Kaygı', value: 'Yüksek', color: '#D88A61' },
      { label: 'Uyku', value: 'Düşük', color: '#D9A258' },
      { label: 'Dikkat', value: 'Orta', color: '#6F90B1' },
    ],
    overlayNote: 'Verileriniz birlikte yorumlanır',
  },
  {
    id: 's2',
    eyebrow: 'Uyku ve günlük enerji',
    heading: 'Uyku Düzeniniz,\nGünlük Temponuzu Etkiler',
    desc: 'Uyku kalitesindeki küçük değişimler bile sabah enerjinizi, dikkat sürenizi ve zihinsel dayanıklılığınızı etkileyebilir.',
    sub: 'Uyku ve enerji birlikte değerlendirilir.',
    image: 'https://images.unsplash.com/photo-1758691462321-9b6c98c40f7e?w=900&q=85&auto=format&fit=crop',
    overlayTitle: 'Uyku ve Enerji',
    overlayText: 'Düşen uyku kalitesi, sabah enerjinizi ve gün içi odaklanmanızı etkiliyor olabilir.',
    overlayHints: [
      { label: 'Uyku Kalitesi', value: 'Düşük', color: '#D9A258' },
      { label: 'Sabah Enerjisi', value: 'Azalmış', color: '#D88A61' },
    ],
    overlayNote: 'Değişimler fark edilir, nedenler yorumlanır',
  },
  {
    id: 's3',
    eyebrow: 'Hafıza ve odak',
    heading: 'Unutkanlık ve Dikkat\nBirlikte Okunur',
    desc: 'Kısa süreli unutkanlık, dikkat dağınıklığı ve zihinsel yorgunluk çoğu zaman aynı tablo içinde birlikte değerlendirilmelidir.',
    sub: 'Tek sonuca değil, bütün tabloya bakılır.',
    image: 'https://images.unsplash.com/photo-1615177393114-bd2917a4f74a?w=900&q=85&auto=format&fit=crop',
    overlayTitle: 'Hafıza ve Dikkat',
    overlayText: 'Kısa süreli unutkanlık ve dikkat değişimleri birlikte yorumlandığında zihinsel yorgunluk öne çıkıyor olabilir.',
    overlayHints: [
      { label: 'Hafıza', value: 'Dalgalı', color: '#6F90B1' },
      { label: 'Odak', value: 'Zayıf', color: '#D88A61' },
    ],
    overlayNote: 'Bilişsel yorgunluk analizi',
  },
  {
    id: 's4',
    eyebrow: 'Günlük takip ve öneriler',
    heading: 'Küçük Adımlarla\nSüreç Takibi',
    desc: 'Sistem yalnızca yorum sunmaz; günlük öneriler, küçük görevler ve düzenli check-in ile ilerlemeyi takip etmenize yardımcı olur.',
    sub: 'Her gün için sade ve uygulanabilir öneriler.',
    image: 'https://images.unsplash.com/photo-1659989693409-5adc97274bed?w=900&q=85&auto=format&fit=crop',
    overlayTitle: 'Bugün İçin Öneri',
    overlayText: 'Bugün 5–10 dakikalık bir nefes egzersizi ile başlamak zihinsel yükünüzü azaltabilir.',
    overlayHints: [
      { label: 'Görev', value: 'Nefes egzersizi', color: '#5A9E7A' },
      { label: 'Süre', value: '10 dk', color: '#6F90B1' },
    ],
    overlayNote: 'Kişisel günlük plan',
  },
];

/* ── Overlay Card ── */
const HeroOverlayCard = ({ slide }) => (
  <div className="hs-overlay" data-testid="hs-overlay">
    <h4 className="hs-overlay-title">{slide.overlayTitle}</h4>
    <p className="hs-overlay-text">{slide.overlayText}</p>
    <div className="hs-overlay-hints">
      {slide.overlayHints.map((h, i) => (
        <div className="hs-overlay-hint" key={i}>
          <span className="hs-overlay-dot" style={{ background: h.color }} />
          <span className="hs-overlay-label">{h.label}</span>
          <span className="hs-overlay-val" style={{ color: h.color }}>{h.value}</span>
        </div>
      ))}
    </div>
    <p className="hs-overlay-note">{slide.overlayNote}</p>
  </div>
);

/* ── Dots ── */
const HeroDots = ({ total, active, onSelect }) => (
  <div className="hs-dots" data-testid="hs-dots">
    {Array.from({ length: total }, (_, i) => (
      <button
        key={i}
        className={`hs-dot ${i === active ? 'hs-dot-active' : ''}`}
        onClick={() => onSelect(i)}
        data-testid={`hs-dot-${i}`}
      />
    ))}
  </div>
);

/* ══════════════════════════════════════════
   HERO STAGE SLIDER
   ══════════════════════════════════════════ */
const HeroStageSlider = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = HERO_SLIDES.length;

  const next = useCallback(() => setActive(p => (p + 1) % total), [total]);
  const prev = useCallback(() => setActive(p => (p - 1 + total) % total), [total]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setActive(p => (p + 1) % total), 5500);
    return () => clearInterval(id);
  }, [paused, total]);

  const slide = HERO_SLIDES[active];

  return (
    <div className="hs-wrap" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      {/* Hero Stage */}
      <section className="hs-stage" data-testid="hs-stage">
        <div className="hs-container">
          {/* Left Column — Text (sabit yükseklik) */}
          <div className="hs-left">
            <div className="hs-left-inner" key={slide.id}>
              <p className="hs-eyebrow">{slide.eyebrow}</p>
              <h1 className="hs-heading" data-testid="hs-heading">
                {slide.heading.split('\n').map((l, i, a) => (
                  <span key={i}>{l}{i < a.length - 1 && <br />}</span>
                ))}
              </h1>
              <p className="hs-desc" data-testid="hs-desc">{slide.desc}</p>
              <p className="hs-sub">{slide.sub}</p>
            </div>
            <button className="hs-cta" onClick={() => navigate('/register')} data-testid="hs-cta">
              Analizime Başla <ArrowRight size={18} />
            </button>
          </div>

          {/* Right Column — Image + Overlay + Arrows */}
          <div className="hs-right">
            <div className="hs-image-wrap">
              <div className="hs-image-panel" key={slide.id + '-img'}>
                <img src={slide.image} alt={slide.overlayTitle} />
                <HeroOverlayCard slide={slide} />
              </div>
              {/* Arrows — sol/sağ kenar, dikey orta */}
              <button className="hs-arrow hs-arrow-left" onClick={prev} aria-label="Önceki" data-testid="hs-nav-prev">
                <ChevronLeft size={22} />
              </button>
              <button className="hs-arrow hs-arrow-right" onClick={next} aria-label="Sonraki" data-testid="hs-nav-next">
                <ChevronRight size={22} />
              </button>
            </div>
          </div>
        </div>

        <HeroDots total={total} active={active} onSelect={setActive} />
      </section>
    </div>
  );
};

/* ═════ TRUST ROW ═════ */
const TrustRow = () => (
  <div className="hp-trust-wrap">
    <div className="hp-trust" data-testid="hp-trust">
      {[
        { icon: <Shield size={18} strokeWidth={1.8} />, t: 'Bilimsel Temelli' },
        { icon: <CheckCircle size={18} strokeWidth={1.8} />, t: 'Kişiye Özel Değerlendirme' },
        { icon: <Eye size={18} strokeWidth={1.8} />, t: 'Gizliliğiniz Korunur' },
      ].map((x, i) => (
        <div key={i} className="hp-trust-item">
          <div className="hp-trust-icon">{x.icon}</div>
          <span>{x.t}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ═════ HOW IT WORKS ═════ */
const howCards = [
  { n: '1', title: 'Testleri Uygula', desc: 'Stres, uyku, dikkat, unutkanlık ve odaklanma gibi temel alanlarda kısa değerlendirmeleri tamamlayın.', snippetLabel: 'Kaygı Testi', snippetValue: '8 soru', snippetColor: '#D88A61' },
  { n: '2', title: 'Verileriniz Birlikte Yorumlanır', desc: 'Sistem yalnızca tek bir sonuca değil, testlerinize ve günlük verilerinize birlikte bakarak genel tabloyu yorumlar.', snippetLabel: 'Genel Tablo', snippetValue: '3 alan', snippetColor: '#6F90B1' },
  { n: '3', title: 'Size Özel Öneriler Oluşur', desc: 'Günlük takip, küçük görevler ve açıklayıcı yorumlarla nereden başlamanız gerektiğini görün.', snippetLabel: 'Bugünkü Öneri', snippetValue: 'Nefes egzersizi', snippetColor: '#5A9E7A' },
];

const HowItWorksSection = () => (
  <section className="hp-how-section" id="nasil">
    <div className="hp-how-inner">
      <div className="hp-section-title"><div className="hp-title-line" /><h2>Nasıl Çalışır?</h2><div className="hp-title-line" /></div>
      <div className="hp-how-grid">
        {howCards.map((c, i) => (
          <div key={i} className="hp-how-card">
            <div className="hp-badge">{c.n}</div>
            <h3>{c.title}</h3>
            <p>{c.desc}</p>
            <div className="hp-how-snippet">
              <div className="hp-how-snippet-dot" style={{ background: c.snippetColor }} />
              <span className="hp-how-snippet-label">{c.snippetLabel}</span>
              <span className="hp-how-snippet-value">{c.snippetValue}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ═════ INSIGHT ═════ */
const ExampleInsightSection = () => (
  <section className="hp-insight-section" id="ozellikler">
    <div className="hp-insight-inner">
      <div className="hp-insight-img"><img src={MEDITATION_IMG} alt="Meditasyon" loading="lazy" /></div>
      <div className="hp-insight-content">
        <div className="hp-quote-mark">&ldquo;</div>
        <p className="hp-insight-text">Kendinizi son günlerde daha yorgun hissediyor olabilirsiniz. Dikkat ve kaygı alanındaki değişimler birlikte değerlendirildiğinde, bugün kısa bir nefes egzersiziyle başlamak iyi gelebilir.</p>
        <div className="hp-insight-tags">
          <div className="hp-insight-tag" style={{ background: 'rgba(216,138,97,0.1)', color: '#D88A61', borderColor: 'rgba(216,138,97,0.2)' }}><Activity size={13} /> Kaygı: Orta</div>
          <div className="hp-insight-tag" style={{ background: 'rgba(217,162,88,0.1)', color: '#D9A258', borderColor: 'rgba(217,162,88,0.2)' }}><Moon size={13} /> Uyku: Düşük</div>
          <div className="hp-insight-tag" style={{ background: 'rgba(111,144,177,0.1)', color: '#6F90B1', borderColor: 'rgba(111,144,177,0.2)' }}><Brain size={13} /> Dikkat: Zayıf</div>
        </div>
      </div>
    </div>
  </section>
);

/* ═════ ANALYSIS CARDS ═════ */
const analysisCards = [
  {
    title: 'Kaygı Seviyesi',
    value: 'Yüksek',
    color: '#D88A61',
    desc: 'Son günlerde artan zihinsel yük, kaygı düzeyinizin normalin üzerine çıkmasına neden olmuş olabilir.',
    bars: [
      { label: 'Bu hafta', w: 82 },
      { label: 'Geçen hafta', w: 58 },
      { label: 'Ortalama', w: 45 },
    ],
    tip: 'Düzenli nefes egzersizi önerilir',
  },
  {
    title: 'Uyku Kalitesi',
    value: 'Düşük',
    color: '#D9A258',
    desc: 'Uyku sürenizde ve dinlenme kalitenizde belirgin bir düşüş fark edildi. Bu durum gün içi enerjinizi etkileyebilir.',
    bars: [
      { label: 'Uyku süresi', w: 38 },
      { label: 'Dinlenme', w: 42 },
      { label: 'Düzen', w: 55 },
    ],
    tip: 'Yatış saatini düzenlemeyi deneyin',
  },
  {
    title: 'Odaklanma',
    value: 'Zayıf',
    color: '#6F90B1',
    desc: 'Dikkat sürenizde düşüş ve kısa süreli unutkanlık birlikte görülüyor. Zihinsel yorgunlukla bağlantılı olabilir.',
    bars: [
      { label: 'Dikkat süresi', w: 48 },
      { label: 'Hafıza', w: 52 },
      { label: 'Tepki hızı', w: 62 },
    ],
    tip: 'Kısa molalar ve odak egzersizleri önerilir',
  },
];

const AnalysisCardsSection = () => (
  <section className="hp-analysis-section">
    <div className="hp-analysis-inner">
      {/* Section header */}
      <div className="hp-analysis-header">
        <h2 className="hp-analysis-heading">Analiz Sonuçlarınızı Anında Görün</h2>
        <p className="hp-analysis-subtext">Testleriniz tamamlandığında sistem verilerinizi birlikte değerlendirerek size net bir tablo sunar.</p>
      </div>

      {/* Cards */}
      <div className="hp-analysis-grid">
        {analysisCards.map((c, i) => (
          <div key={i} className="hp-analysis-card" data-testid={`hp-analysis-card-${i}`}>
            {/* Color accent line */}
            <div className="hp-analysis-accent" style={{ background: c.color }} />

            {/* Title row */}
            <div className="hp-analysis-title-row">
              <h3 className="hp-analysis-title">{c.title}</h3>
              <span className="hp-analysis-badge" style={{ color: c.color, background: `${c.color}12`, borderColor: `${c.color}25` }}>{c.value}</span>
            </div>

            {/* Description */}
            <p className="hp-analysis-desc">{c.desc}</p>

            {/* Bars */}
            <div className="hp-analysis-bars">
              {c.bars.map((bar, j) => (
                <div className="hp-analysis-bar-row" key={j}>
                  <span className="hp-analysis-bar-label">{bar.label}</span>
                  <div className="hp-analysis-bar-track">
                    <div className="hp-analysis-bar-fill" style={{ width: `${bar.w}%`, background: c.color }} />
                  </div>
                  <span className="hp-analysis-bar-pct" style={{ color: c.color }}>{bar.w}%</span>
                </div>
              ))}
            </div>

            {/* Tip */}
            <div className="hp-analysis-tip">
              <span className="hp-analysis-tip-dot" style={{ background: c.color }} />
              <span>{c.tip}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ═════ FINAL CTA ═════ */
const FinalCTASection = () => {
  const navigate = useNavigate();
  return (
    <section className="hp-final-cta-section">
      <div className="hp-final-cta" data-testid="hp-final-cta">
        <p>Detaylı değerlendirmeyi görmek ve size özel yol haritanızı oluşturmaya başlamak için hesabınızı oluşturun</p>
        <button className="hp-final-cta-btn" onClick={() => navigate('/register')}>Ücretsiz Hesap Oluştur <ChevronRight size={18} /></button>
        <div className="hp-final-badges">
          <div className="hp-final-badge"><Shield size={15} /> Bilimsel Yöntemler</div>
          <div className="hp-final-badge"><Eye size={15} /> Gizliliğiniz Korunur</div>
          <div className="hp-final-badge"><CheckCircle size={15} /> Kişisel Takip Deneyimi</div>
        </div>
      </div>
    </section>
  );
};

/* ═════ FOOTER ═════ */
const HPFooter = () => (
  <div className="hp-footer-wrap">
    <div className="hp-footer">
      <div>
        <div className="hp-footer-brand" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/logo/icon-dark.svg" alt="" style={{ height: 22, width: 22 }} />
          <span>Doktor<span style={{ color: '#D9A258' }}>Cebinde</span></span>
        </div>
        <p className="hp-footer-desc">Kişisel sağlık takibi ve açıklayıcı değerlendirme deneyimi.</p>
      </div>
      <div className="hp-footer-links"><div className="hp-footer-col"><a href="/hakkimizda">Hakkında</a><a href="/privacy">Gizlilik</a><a href="/iletisim">İletişim</a></div></div>
    </div>
    <div className="hp-footer-bottom">Bu platform tıbbi tanı koymaz. Klinik değerlendirme yerine geçmez.</div>
  </div>
);

/* ═════ MAIN ═════ */
export default function Homepage() {
  return (
    <div className="hp-page">
      <Helmet>
        <title>DoktorCebinde — Kişisel Zihinsel Sağlık Takibi</title>
        <meta name="description" content="Stres, uyku, unutkanlık, dikkat ve günlük sağlık verilerinizi bir araya getirerek size özel değerlendirme sunan dijital sağlık asistanı." />
      </Helmet>
      <Header />
      <HeroStageSlider />
      <TrustRow />
      <HowItWorksSection />
      <ExampleInsightSection />
      <AnalysisCardsSection />
      <FinalCTASection />
      <HPFooter />
    </div>
  );
}
